/**
 * The `GET /api/overview` wire contract (seam S1, slice 15).
 *
 * This mirrors `OverviewResponseZ` in api/src/modules/overview/routes.ts field for
 * field. Neither side invents a key: the server zod-parses what it sends, and this
 * is the only description of that shape on the client.
 *
 * UNITS ARE BASE UNITS, and the names say so. `distanceM` is METRES and
 * `movingSec` is SECONDS — the server never ships a pre-formatted or pre-rounded
 * number, exactly as trips ship km/h rather than the stored tenths. Formatting is
 * `features/trips/format.ts`'s job and happens once, at render.
 */
export interface OverviewSummary {
  alerts: {
    /** Every crossing in the window, both severities. */
    total: number;
    /**
     * The alerting subset — the SAME set `GET /api/alerts?severity=alert` returns,
     * graded server-side by the one shared `ALERTABILITY` predicate. This is why
     * the tile and the Alerts page cannot disagree in front of a customer.
     */
    critical: number;
  };
  trips: {
    /** Trips OVERLAPPING the window — the same set the Trips page lists. */
    count: number;
    /** Metres. */
    distanceM: number;
    /** Seconds of MOVING time, not wall-clock duration. */
    movingSec: number;
  };
  serverTime: string;
}

/**
 * A BOUNDARY CHECK, and it exists because this slice's own tests caught the crash.
 *
 * `apiJson<T>` casts — it does not verify. So a body of the wrong shape (a
 * different endpoint's response through a misrouted proxy, a truncated payload, a
 * `{}` from an error path that forgot its status code) reached
 * `data.alerts.total` and threw inside render, taking the WHOLE Overview down:
 * the map link, the fleet list, the vehicle counts, everything. That is a worse
 * outcome than the missing tile by a wide margin, and it was found by a test
 * feeding a well-formed body from a DIFFERENT endpoint — not by review.
 *
 * This is the board's ranked-#2 open item ("every unit test in this codebase
 * supplies a well-formed response; that is systematic, not one missed case"),
 * appearing exactly where it was predicted to. The board also says that item wants
 * a STRUCTURAL answer — validating `apiJson` itself, or a zod boundary parse — and
 * this is deliberately NOT that: it is one hook's guard, hand-written so the
 * landing screen does not gain a validation dependency on the login critical path.
 * The structural fix stays open and stays board item 2.
 *
 * A body that fails this becomes a QUERY ERROR, which the Overview already renders
 * as "—" (not-yet-known), never as a zero and never as a crash.
 */
export function isOverviewSummary(v: unknown): v is OverviewSummary {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  const num = (x: unknown) => typeof x === 'number' && Number.isFinite(x);
  const a = o.alerts as Record<string, unknown> | undefined;
  const t = o.trips as Record<string, unknown> | undefined;
  if (typeof a !== 'object' || a === null || typeof t !== 'object' || t === null) return false;
  return (
    num(a.total) &&
    num(a.critical) &&
    num(t.count) &&
    num(t.distanceM) &&
    num(t.movingSec)
  );
}
